import {ApiError, OnUploadProgress} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {FileResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/FileResponse';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {VisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {
    SurfaceDropContext,
    SurfacePoint,
    SurfaceSize
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {
    SurfaceFileUploadState,
    UploadFailedReason,
    UploadFailedReasonEnum,
    UploadId,
    UploadInstance,
    UploadStatus
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceFileUploadState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Bounds} from '@mintdata/mint-kit/api/models/Bounds';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {PropertyPanelTabId} from '@mintdata/mint-kit/api/properties/PropertyPanelTabId';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {action, runInAction} from 'mobx';

export class SurfaceFileUploadController
{
    public async handlePastingFromClipboardFiles(event:React.ClipboardEvent<HTMLDivElement>):Promise<void>
    {
        const {surfaceArea, surfaceSelection, surfacePropertiesPanel} = controllers.ui;

        let accepted:File[] = [];
        let rejected:File[] = [];

        if( event.clipboardData )
        {
            const files:File[] = Array.from(event.clipboardData.files);

            // currently only images are supported
            accepted = files.filter(file => file.type.startsWith('image/'));
            rejected = files.filter(file => !file.type.startsWith('image/'));

            if( rejected.length )
                return this.uploadFailed(null, UploadFailedReasonEnum.FileTypeIsNotSupported, rejected[0]);
        }

        const file:File = accepted[0];

        if( !file )
            return;

        const selectedBoxes:Box[] = surfaceSelection.getSelectedBoxes();

        if( selectedBoxes.length > 1 )
            return;

        const page:Page = surfaceArea.getCurrentLayerPage();

        let parentBox:Box = selectedBoxes.length === 0
            ? surfaceArea.getCurrentLayerPage().artboard.viewport
            : selectedBoxes[0];

        const imageComponent:VisualComponent = this._getImageComponent();
        const componentSize:SurfaceSize = surfaceArea.componentInitialSize(imageComponent);
        const selectedTabId:PropertyPanelTabId = surfacePropertiesPanel.getSelectedTabId();

        if( parentBox.component === imageComponent )
            await this.loadFileIntoBox(file, parentBox, selectedTabId);
        else
        {
            if( !surfaceArea.isBoxContainer(parentBox) )
                parentBox = surfaceArea.findClosestBoxContainer(parentBox);

            const position:SurfacePoint = SurfacePoint.from(
                Math.round(parentBox.bounds.width / 2 - componentSize.width / 2),
                Math.round(parentBox.bounds.height / 2 - componentSize.height / 2)
            );

            const box:Box = await surfaceArea.addNewBox(page, parentBox, null, imageComponent, null, position, componentSize);
            await this.loadFileIntoBox(file, box);

            surfaceSelection.setSingleBoxSelection(box);
        }
    }

    public async handleDroppedOnSurfaceFiles(event:React.DragEvent<HTMLElement>,
                                             accepted:File[],
                                             rejected:File[],
                                             page?:Page,
                                             parentBox?:Box):Promise<void>
    {
        const {surfaceArea, surfaceSelection} = controllers.ui;

        this.setIsFileDraggingOverSurface(false);

        if( rejected.length )
            rejected.forEach((file:File) => this.uploadFailed(null, UploadFailedReasonEnum.FileTypeIsNotSupported, file));

        if( !accepted.length )
            return;

        const position:SurfacePoint = surfaceArea.adjustSurfacePointToPanZoom(SurfacePoint.from(event.clientX, event.clientY));

        if( !page )
            page = surfaceArea.getCurrentLayerPage();

        if( !parentBox )
        {
            const viewport:Box = page.artboard.viewport;
            const viewportBounds:Bounds = viewport.bounds;
            const positionWithinCanvas:SurfacePoint = surfaceArea.computePositionWithinCanvas(position, true);

            if( surfaceArea.hasSurfacePointIntersectionWithBounds(positionWithinCanvas, viewportBounds) )
                parentBox = viewport;
        }

        // need to switch by file type (image, video)
        const imageComponent:VisualComponent = this._getImageComponent();

        const dropContext = new SurfaceDropContext(page, parentBox, null, null, position, imageComponent);

        const boxes:Box[] = await Promise.all(accepted.map(async (file:File) =>
        {
            const box:Box = await surfaceArea.dropComponent(dropContext);

            await this.loadFileIntoBox(file, box);

            return box;
        }));

        surfaceSelection.setSingleBoxSelection(boxes[boxes.length - 1]);
    }

    public async handleDroppedOnBoxFiles(accepted:File[],
                                         rejected:File[],
                                         box:Box,
                                         selectedTabId?:PropertyPanelTabId):Promise<void>
    {
        this.setIsFileDraggingOverPropertiesPanel(false);

        if( [...accepted, ...rejected].length > 1 )
            return this.uploadFailed(null, UploadFailedReasonEnum.MultipleIsNotAllowed);

        if( rejected.length )
            return this.uploadFailed(null, UploadFailedReasonEnum.FileTypeIsNotSupported, rejected[0]);

        return this.loadFileIntoBox(accepted[0], box, selectedTabId);
    }

    public async loadFileIntoBox(file:File, box:Box, selectedTabId?:PropertyPanelTabId):Promise<void>
    {
        const {uploadedImages} = controllers.ui.assets;
        const {component} = box;

        switch( component.name )
        {
            case 'Image':
            {
                const hideBoxLoader = this._showBoxLoader(box);

                const response:FileResponse | Error = await this.uploadFile(file);

                if( response instanceof FileResponse )
                {
                    // const url:AString = AString.fromString(new GraphicRef(response.id, GraphicType.UploadedImage).toString());
                    const url:AString = AString.fromString(response.accessUrl);

                    uploadedImages.saveUploadedImage(response.id, file.name, response.accessUrl);

                    const model:Prop<any> = box.componentDesc.properties.get('model');

                    if( !selectedTabId )
                    {
                        model.setPropValue(PropPath.fromString('url'), url);
                        model.setPropValue(PropPath.fromString('hoverState.url'), url);
                        model.setPropValue(PropPath.fromString('activeState.url'), url);
                    }
                    else if( selectedTabId === PropertyPanelTabId.Default )
                        model.setPropValue(PropPath.fromString('url'), url);
                    else if( selectedTabId === PropertyPanelTabId.Hover )
                        model.setPropValue(PropPath.fromString('hoverState.url'), url);
                    else if( selectedTabId === PropertyPanelTabId.Active )
                        model.setPropValue(PropPath.fromString('activeState.url'), url);
                }

                hideBoxLoader();

                break;
            }
        }
    }

    public async uploadFile(file:File):Promise<FileResponse | Error>
    {
        const {toasts} = controllers.ui;

        this.disposeCompletedUploads();

        const uploadInstance = this._createUpload(file, UploadStatus.Uploading);

        const formData = new FormData();

        formData.append('filename', file.name);
        formData.append('size', file.size.toString());
        formData.append('contents', file);

        try
        {
            toasts.showFileUploadToast(uploadInstance);

            const onProgress:OnUploadProgress = action((e:ProgressEvent) => (
                uploadInstance.uploadProgress = Math.ceil((e.loaded / e.total) * 100)
            ));

            const orgId:string = rootState.userSession.currentOrganization.siteId;

            const response = await API.files.uploadFile(orgId, formData, onProgress) as FileResponse | ApiError;

            if( uploadInstance.isUploadCancelled )
            {
                this.disposeUpload(uploadInstance.id);

                return new Error('Upload file request has been cancelled');
            }

            if( ApiError.isError(response) )
            {
                const message:string = (response as ApiError).message;
                this.uploadFailed(uploadInstance.id, message);

                return new Error(message);
            }
            else
            {
                return runInAction(() =>
                {
                    const {accessUrl} = response as FileResponse;

                    uploadInstance.uploadedFileUrl = accessUrl;
                    uploadInstance.status = UploadStatus.UploadSuccess;

                    return response as FileResponse;
                });
            }
        }
        catch( error )
        {
            runInAction(() => this.uploadFailed(uploadInstance.id, error.toString()));

            return error;
        }
    }

    @action
    public uploadFailed(id:UploadId = null, reason:UploadFailedReason, file:File = null):void
    {
        const {toasts} = controllers.ui;

        let uploadInstance:UploadInstance;

        if( id )
        {
            uploadInstance = this._getUpload(id);
            uploadInstance.status = UploadStatus.UploadFailed;

            return;
        }
        else
        {
            uploadInstance = this._createUpload(
                file,
                UploadStatus.UploadFailed,
                reason
            );
        }

        toasts.showFileUploadToast(uploadInstance);
    }

    @action
    public disposeUpload(id:UploadId):boolean
    {
        const state:SurfaceFileUploadState = this._getState();

        return state.instances.delete(id);
    }

    @action
    public disposeCompletedUploads():boolean
    {
        const {toasts} = controllers.ui;
        const state:SurfaceFileUploadState = this._getState();

        state.instances.forEach(({id, isUploading}) =>
        {
            if( !isUploading )
            {
                this.disposeUpload(id);
                toasts.hideFileUploadToast(id);
            }
        });

        return true;
    }

    @action
    public cancelUploading(id:UploadId)
    {
        const uploadInstance = this._getUpload(id);

        uploadInstance.status = UploadStatus.UploadCancelled;
    }

    @action
    public setIsFileDraggingOverSurface(value:boolean):void
    {
        const state:SurfaceFileUploadState = this._getState();

        state.isFileDraggingOverSurface = value;
    }

    public getIsFileDraggingOverSurface():boolean
    {
        return this._getState().isFileDraggingOverSurface;
    }

    @action
    public setIsFileDraggingOverPropertiesPanel(value:boolean):void
    {
        const state:SurfaceFileUploadState = this._getState();

        state.isFileDraggingOverPropertiesPanel = value;
    }

    public getIsFileDraggingOverPropertiesPanel():boolean
    {
        return this._getState().isFileDraggingOverPropertiesPanel;
    }

    @action
    private _createUpload(file:File,
                          status:UploadStatus,
                          failedReason?:UploadFailedReason):UploadInstance
    {
        const state:SurfaceFileUploadState = this._getState();
        const uploadInstance = new UploadInstance(file, status, failedReason);

        state.instances.set(uploadInstance.id, uploadInstance);

        return uploadInstance;
    }

    // poor loading while file is uploading
    private _showBoxLoader(box:Box):() => void
    {
        const {loaderProp} = box.componentDesc;
        const isLoadingPath:PropPath = PropPath.fromString('isLoading');
        const spinnerSizePath:PropPath = PropPath.fromString('spinnerSize');

        const isLoading:ABoolean = loaderProp.getPropValue(isLoadingPath);
        const spinnerSize:ABoolean = loaderProp.getPropValue(spinnerSizePath);

        loaderProp.setPropValue(isLoadingPath, ABoolean.true());
        loaderProp.setPropValue(spinnerSizePath, Units.fromPixels(64));

        return () =>
        {
            loaderProp.setPropValue(isLoadingPath, isLoading);
            loaderProp.setPropValue(spinnerSizePath, spinnerSize);
        };
    }

    private _getUpload(id:UploadId):UploadInstance
    {
        const state:SurfaceFileUploadState = this._getState();

        return state.instances.get(id);
    }

    private _getImageComponent():VisualComponent
    {
        const {artifacts} = controllers.ui;

        return Array.from(artifacts.getVisualComponents().values()).find(({name}) => 'Image' === name);
    }

    public _getState():SurfaceFileUploadState
    {
        return rootState.ui.visualDesigner.surfaceEditor.area.surfaceFileUploadState;
    }
}
